import z from "zod";

/**
 * Polymarket API types based on Gamma Markets API
 * API Docs: https://docs.polymarket.com/developers/gamma-markets-api
 */
export const PolymarketMarketSchema = z.object({
  id: z.string(),
  question: z.string(),
  conditionId: z.string(),
  slug: z.string(),
  resolutionSource: z.string().optional(),
  endDate: z.string(),
  startDate: z.string(),
  image: z.string().optional(),
  icon: z.string().optional(),
  description: z.string(),
  outcomes: z.string(), // JSON string array e.g. '["Yes", "No"]'
  outcomePrices: z.string(), // JSON string array e.g. '["0.45", "0.55"]'
  volume: z.string(),
  volumeNum: z.number(),
  liquidity: z.string().optional(),
  liquidityNum: z.number().optional(),
  active: z.boolean(),
  closed: z.boolean(),
  archived: z.boolean(),
  new: z.boolean(),
  featured: z.boolean(),
  restricted: z.boolean(),

  // Volume metrics
  volume24hr: z.number().optional(),
  volume1wk: z.number().optional(),
  volume1mo: z.number().optional(),
  volume1yr: z.number().optional(),

  // Price changes
  oneDayPriceChange: z.number().optional(),
  oneWeekPriceChange: z.number().optional(),
  oneMonthPriceChange: z.number().optional(),

  // Trading data
  lastTradePrice: z.number().optional(),
  bestBid: z.number().optional(),
  bestAsk: z.number().optional(),
  spread: z.number().optional(),

  // For grouped markets (multiple choice)
  groupItemTitle: z.string().optional(),
  groupItemThreshold: z.string().optional(),

  // Timestamps
  createdAt: z.string(),
  updatedAt: z.string(),
  endDateIso: z.string(),
  startDateIso: z.string(),

  // Order book settings
  enableOrderBook: z.boolean().optional(),
  acceptingOrders: z.boolean().optional(),
  orderPriceMinTickSize: z.number().optional(),
  orderMinSize: z.number().optional(),

  // UMA resolution
  umaResolutionStatus: z.string().optional(),
  marketMakerAddress: z.string().optional(),
  clobTokenIds: z.string().optional(),
  competitive: z.number().optional(),
  negRisk: z.boolean().optional(),
  commentCount: z.number().optional(),
});

export type PolymarketMarket = z.infer<typeof PolymarketMarketSchema>;

export const PolymarketEventSchema = z.object({
  id: z.string(),
  ticker: z.string(),
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  resolutionSource: z.string().optional(),

  // Dates
  startDate: z.string(),
  creationDate: z.string(),
  endDate: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),

  // Images
  image: z.string().optional(),
  icon: z.string().optional(),

  // Status flags
  active: z.boolean(),
  closed: z.boolean(),
  archived: z.boolean(),
  new: z.boolean(),
  featured: z.boolean(),
  restricted: z.boolean(),

  // Financial metrics
  liquidity: z.number(),
  liquidityClob: z.number().optional(),
  volume: z.number(),
  openInterest: z.number(),
  volume24hr: z.number(),
  volume1wk: z.number(),
  volume1mo: z.number(),
  volume1yr: z.number(),

  // Market type
  competitive: z.number(),
  enableOrderBook: z.boolean(),
  negRisk: z.boolean(),
  negRiskMarketID: z.string().optional(),

  // Metadata
  commentCount: z.number(),
  sortBy: z.string().optional(),

  // Markets array - nested markets for this event
  markets: z.array(PolymarketMarketSchema),
});

export type PolymarketEvent = z.infer<typeof PolymarketEventSchema>;

export interface PolymarketTag {
  id: string;
  label: string | null;
  slug: string | null;
  forceShow?: boolean | null;
  forceHide?: boolean | null;
  isCarousel?: boolean | null;
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Event list API response
 */
export const PolymarketEventsResponseSchema = z.array(PolymarketEventSchema);
export type PolymarketEventsResponse = z.infer<typeof PolymarketEventsResponseSchema>;

/**
 * Tags list API response
 */
export type PolymarketTagsResponse = PolymarketTag[];

/**
 * Market type detection helper
 */
export enum PolymarketMarketType {
  BINARY = "binary",
  MULTIPLE_CHOICE = "multiple_choice",
}

/**
 * Helper to determine market type based on event structure
 * - Binary: Single market with 2 outcomes OR 2 markets in event
 * - Multiple Choice: Event with 3+ markets
 */
export function getMarketType(event: PolymarketEvent): PolymarketMarketType {
  const marketCount = event.markets?.length;

  if (marketCount === 1) {
    // Single market - check outcomes
    const market = event.markets[0];
    if (!market) return PolymarketMarketType.BINARY;

    try {
      const outcomes = JSON.parse(market.outcomes);
      return outcomes.length === 2 ? PolymarketMarketType.BINARY : PolymarketMarketType.MULTIPLE_CHOICE;
    } catch {
      return PolymarketMarketType.BINARY;
    }
  }

  // Multiple markets in event = multiple choice
  return marketCount === 2 ? PolymarketMarketType.BINARY : PolymarketMarketType.MULTIPLE_CHOICE;
}

/**
 * Parse outcomes from JSON string
 */
export function parseOutcomes(outcomesJson: string): string[] {
  try {
    return JSON.parse(outcomesJson);
  } catch {
    return [];
  }
}

/**
 * Parse outcome prices from JSON string
 */
export function parseOutcomePrices(pricesJson: string): number[] {
  try {
    const prices = JSON.parse(pricesJson);
    return prices.map((p: string) => parseFloat(p));
  } catch {
    return [];
  }
}

/**
 * Format volume for display (e.g., $1.2M)
 */
export function formatVolume(volume: number | undefined | null): string {
  if (!volume || volume === 0) {
    return "$0";
  }
  if (volume >= 1_000_000) {
    return `$${(volume / 1_000_000).toFixed(1)}m`;
  }
  if (volume >= 1_000) {
    return `$${(volume / 1_000).toFixed(0)}k`;
  }
  return `$${volume.toFixed(0)}`;
}

/**
 * Polymarket withdrawal response from backend
 */
export interface PolymarketWithdrawResponse {
  success: boolean;
  recipientAddress: string;
  amountRaw: string;
  proxyWalletAddress: string;
  currentBalance: number;
}

/**
 * Categories for filtering
 */
export enum PolymarketCategory {
  NEW = "new",
  CRYPTO = "crypto",
  FINANCE = "finance",
  GEOPOLITICS = "geopolitics",
  ALL = "all",
  CULTURE = "culture",
}

/**
 * Category display names
 */
export const POLYMARKET_CATEGORY_LABELS: Record<PolymarketCategory, string> = {
  [PolymarketCategory.NEW]: "New",
  [PolymarketCategory.CRYPTO]: "Crypto",
  [PolymarketCategory.FINANCE]: "Finance",
  [PolymarketCategory.GEOPOLITICS]: "Geopolitics",
  [PolymarketCategory.ALL]: "All",
  [PolymarketCategory.CULTURE]: "Culture",
};

// TODO: Add more tags for each category using SOLO CMS
/**
 * Tag ID mapping for categories using real Polymarket tag IDs
 * These IDs are from the Polymarket /tags API endpoint
 * Source: https://gamma-api.polymarket.com/tags?limit=500
 */
export const POLYMARKET_CATEGORY_TAG_IDS: Record<PolymarketCategory, number[]> = {
  [PolymarketCategory.ALL]: [], // All markets (no tag filter; sorted by volume1wk)
  [PolymarketCategory.NEW]: [], // Computed client-side by date
  // slugs: bitcoin, dogecoin, usdt
  [PolymarketCategory.CRYPTO]: [235, 100150, 21, 1312, 102859, 102860, 39, 818, 136, 101267, 101312],
  // slugs: interest-rates, fed-rates, market-trends, global-economy
  [PolymarketCategory.FINANCE]: [131, 100196, 964, 639, 100328, 120, 600, 107],
  [PolymarketCategory.GEOPOLITICS]: [
    // slugs: uptspt-congress, us-election, trump, us-presidential-election, elections-2024,
    // senate-elections, international-affairs, world-affairs
    766, 24, 2, 126, 1101, 377, 100199, 1396, 366, 100265, 78, 154, 101970, 96, 101794, 103027, 270,
  ],
  [PolymarketCategory.CULTURE]: [
    // slugs: music, nba-championship, nba-finals, internet-culture
    100, 100108, 100240, 1255, 596, 53, 18,
  ],
};
